import { createContext, useContext, FunctionComponent, ReactNode, useState, useEffect, useMemo } from 'react';
import { UseFormReturn, useForm, useFieldArray, UseFieldArrayReturn, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateTransactionModalSchema,
  CreateTransactionModalFormModel,
} from '../components/CreateTransactionModal/CreateTransactionModalFormModel';
import dayjs from 'dayjs';
import { AssetTypeModel } from '@/modules/asset-types/types/AssetTypeModel';
import { assetTypeService } from '@/modules/asset-types/api/asset-types/asset-type.service';
import { LocationModel } from '@/modules/locations/types/LocationModel';
import { locationService } from '@/modules/locations/api/locations/location.service';
import { SelectOption } from '@/components/inputs/GenericSelect';

interface TransactionContextValue {
  form: UseFormReturn<CreateTransactionModalFormModel>;
  fieldArray: UseFieldArrayReturn<CreateTransactionModalFormModel, 'assets', 'id'>;
  assetTypes: AssetTypeModel[];
  locations: LocationModel[];
  hasAvailableAssetTypes: boolean;
  getAvailableAssetTypesForIndex: (index: number) => SelectOption[];
  getAvailableLocationsFor: (field: 'from' | 'to') => SelectOption[];
  resetForm: () => void;
}

const TransactionContext = createContext<TransactionContextValue | undefined>(undefined);

interface TransactionProviderProps {
  children: ReactNode;
  open: boolean;
  isGuestMode?: boolean;
}

const getDefaultValues = (): CreateTransactionModalFormModel => ({
  fromLocationId: undefined,
  toLocationId: undefined,
  date: dayjs(),
  carrierId: undefined,
  documentNumber: '',
  referenceNumber: '',
  notes: '',
  assets: [
    { assetTypeId: undefined, amount: 1 },
    { assetTypeId: undefined, amount: 1 },
  ],
});

export const TransactionProvider: FunctionComponent<TransactionProviderProps> = ({ children, open, isGuestMode = false }) => {
  const [assetTypes, setAssetTypes] = useState<AssetTypeModel[]>([]);
  const [locations, setLocations] = useState<LocationModel[]>([]);

  const form = useForm<CreateTransactionModalFormModel>({
    resolver: zodResolver(CreateTransactionModalSchema),
    mode: 'onSubmit',
    defaultValues: getDefaultValues(),
  });

  const fieldArray = useFieldArray({
    control: form.control,
    name: 'assets',
  });

  // Watch all asset selections
  const allAssets = useWatch({
    control: form.control,
    name: 'assets',
  });

  const fromLocationId = useWatch({
    control: form.control,
    name: 'fromLocationId',
  });

  const toLocationId = useWatch({
    control: form.control,
    name: 'toLocationId',
  });

  // Check if there are any asset types available that haven't been selected yet
  const hasAvailableAssetTypes = useMemo(() => {
    const selectedAssetTypeIds = allAssets?.map((asset) => asset?.assetTypeId).filter((id): id is number => id !== undefined && id > 0) || [];

    return assetTypes.some((assetType) => !selectedAssetTypeIds.includes(assetType.id));
  }, [assetTypes, allAssets]);

  // Get available asset types for a specific row index (excluding types selected in other rows)
  const getAvailableAssetTypesForIndex = useMemo(
    () => (index: number) => {
      const selectedAssetTypeIds =
        allAssets
          ?.map((asset, idx) => (idx !== index ? asset?.assetTypeId : undefined))
          .filter((id): id is number => id !== undefined && id > 0) || [];

      return assetTypes
        .filter((assetType) => !selectedAssetTypeIds.includes(assetType.id))
        .map((assetType) => ({
          id: assetType.id,
          name: assetType.name,
        }));
    },
    [assetTypes, allAssets],
  );

  // Get the location ID that should be excluded from the dropdown
  const getAvailableLocationsFor = useMemo(
    () => (field: 'from' | 'to') => {
      const excludedLocationId =
        field === 'from'
          ? toLocationId && toLocationId > 0
            ? toLocationId
            : undefined
          : fromLocationId && fromLocationId > 0
            ? fromLocationId
            : undefined;

      return locations
        .filter((location) => !excludedLocationId || location.dto.id !== excludedLocationId)
        .map((location) => ({
          id: location.dto.id,
          name: `${location.dto.name} (${location.dto.code})`,
        }));
    },
    [locations, fromLocationId, toLocationId],
  );

  // Fetch asset types and locations when modal opens
  useEffect(() => {
    if (open) {
      const fetchAssetTypes = async () => {
        const response = await assetTypeService.getAll();
        if (response.isSuccess) {
          setAssetTypes(response.payload);
        }
      };

      const fetchLocations = async () => {
        if (isGuestMode) {
          // Guest mode: only fetch locations assigned to the user
          const { balanceService } = await import('@/modules/balances/api/balances/balance.service');
          const { LocationType } = await import('@/modules/locations/api/locations/location.contracts');
          const response = await balanceService.getBalancesForLocations({ query: '' });
          if (response.isSuccess) {
            // Convert BalancesAtLocationModel to LocationModel format
            const guestLocations: LocationModel[] = response.payload.map((balance) => {
              const locationDTO = {
                id: balance.dto.locationId,
                name: balance.dto.locationName,
                code: balance.dto.locationCode,
                description: '',
                type: LocationType.DEPO,
                includeInReturnRate: true,
                children: [],
                isSystem: balance.dto.isSystem,
                createdAt: new Date(),
              };
              return new LocationModel(locationDTO);
            });
            setLocations(guestLocations);
          }
        } else {
          // Normal mode: fetch all locations
          const response = await locationService.getAll({ query: '' });
          if (response.isSuccess) {
            setLocations(response.payload);
          }
        }
      };

      fetchAssetTypes();
      fetchLocations();
    }
  }, [open, isGuestMode]);

  const resetForm = () => {
    form.reset(getDefaultValues());
  };

  const value: TransactionContextValue = {
    form,
    fieldArray,
    assetTypes,
    locations,
    hasAvailableAssetTypes,
    getAvailableAssetTypesForIndex,
    getAvailableLocationsFor,
    resetForm,
  };

  return <TransactionContext.Provider value={value}>{children}</TransactionContext.Provider>;
};

export const useTransactionContext = (): TransactionContextValue => {
  const context = useContext(TransactionContext);
  if (!context) {
    throw new Error('useTransactionContext must be used within a TransactionProvider');
  }
  return context;
};
